import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '~/lib/api';

export interface Me {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string | null;
    emailVerified: boolean;
  };
  tenant: {
    id: string;
    name: string;
    website: string | null;
    status: 'pending_kyb' | 'active' | 'suspended';
    termsAcceptedAt: string | null;
  };
}

export type KybStatus =
  | 'none'
  | 'submitted'
  | 'in_review'
  | 'approved'
  | 'rejected';

interface SessionContextValue {
  me: Me | null;
  /**
   * Dashboard-side KYB case status. `none` means the tenant hasn't submitted
   * yet; `submitted`/`in_review` mean it's with reviewers; `rejected` needs
   * resubmit. Tenant.status is a different signal — it stays pending_kyb
   * until approval, so UI copy needs BOTH to pick the right nudge.
   */
  kybStatus: KybStatus;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * Loads /dashboard/me once on mount and keeps the result in context. The
 * session itself is a server-set HttpOnly cookie we cannot read, so "am I
 * signed in?" is answered by whether /dashboard/me returns 200. Pages read
 * the user + tenant through useSession() instead of hardcoding. signOut()
 * POSTs /auth/logout (best-effort — the cookie's own expiry is the fallback)
 * and sends the user to /login.
 */
export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const navigate = useNavigate();
  const [me, setMe] = useState<Me | null>(null);
  const [kybStatus, setKybStatus] = useState<KybStatus>('none');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      // Fetch in parallel: /me is the authentication signal; /kyb tells us
      // whether a case is in flight so the TopBar + banner can read "In
      // review" instead of nagging the user to start.
      const [next, kyb] = await Promise.all([
        api<Me>('/dashboard/me'),
        api<{ status: KybStatus }>('/dashboard/kyb').catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 404) return null;
          throw err;
        }),
      ]);
      setMe(next);
      setKybStatus(kyb?.status ?? 'none');
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setMe(null); // Shell will navigate to /login once loading settles.
        setKybStatus('none');
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not load profile');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch {
      // Best-effort: even if the POST fails the cookie's own expiry will
      // clear the session; a transient network error must not strand the
      // user on an authed page.
    }
    setMe(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<SessionContextValue>(
    () => ({ me, kybStatus, loading, error, refresh, signOut }),
    [me, kybStatus, loading, error, refresh, signOut],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider/>');
  return ctx;
}
